/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useRef, useState } from 'react';
import { Box, Button, IconButton, Link, Paper, Popper, Typography, Zoom } from '@mui/material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { styled } from '@mui/material/styles';
import styles from './HelpIconComponent.module.scss';

interface HelpIconComponentProps {
    content: 'access-group' | 'fit-variant';
}

const helpContent = {
    'access-group': (
        <Box>
            <p>
                Users and scanners are linked to an Access Group.
            </p>
            <p>
                This determines which scans a user can access, i.e.
                Users
                assigned to the Access Group &apos;Acme Staff&apos;, can only see scans from scanners assigned
                to &apos;Acme
                Staff&apos;.
            </p>
            <p>
                If you would like to create more Access Groups, contact support&nbsp;
                <Link href="https://www.bodd.io/contactsupport" target="_blank" rel="noreferrer">here</Link>
            </p>
        </Box>),
    'fit-variant': (
        <Box>
            <p>
                SizeMatch recommendations are based on a chart of body measurement ranges which correlate with specific
                sizes.
            </p>

            <p>
                Some products may fit differently on different body types (i.e, the way a garment is recommended for a
                female body type will be different for a male body type, hence 2 different charts may be used to
                determine the sizing logic).
            </p>

            <p>
                Learn more about fit variants&nbsp;
                <Link href="https://help.bodd.io/fit-variants" target="_blank" rel="noreferrer">here</Link>
            </p>
        </Box>),
};

const StyledPopper = styled(Popper)(({ theme }) => ({ // You can replace with `PopperUnstyled` for lower bundle size.
    zIndex: 9999,
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 11px 15px -7px, rgba(0, 0, 0, 0.14) 0px 24px 38px 3px, rgba(0, 0, 0, 0.12) 0px 9px 46px 8px',
    maxWidth: '375px',
    width: '100%',
    '&[data-popper-placement*="bottom"] .arrow': {
        top: 0,
        left: 0,
        marginTop: '-0.9em',
        width: '3em',
        height: '1em',
        '&::before': {
            borderWidth: '0em 1em 1em 1em',
            borderColor: `transparent transparent ${theme.palette.background.paper} transparent`,
        },
    },
    '&[data-popper-placement*="top"] .arrow': {
        bottom: 0,
        left: 0,
        marginBottom: '-0.9em',
        width: '3em',
        height: '1em',
        '&::before': {
            borderWidth: '1em 1em 0 1em',
            borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
        },
    },
    '&[data-popper-placement*="right"] .arrow': {
        left: 0,
        marginLeft: '-0.9em',
        height: '3em',
        width: '1em',
        '&::before': {
            borderWidth: '1em 1em 1em 0',
            borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
        },
    },
    '&[data-popper-placement*="left"] .arrow': {
        right: 0,
        marginRight: '-0.9em',
        height: '3em',
        width: '1em',
        '&::before': {
            borderWidth: '1em 0 1em 1em',
            borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
        },
    },
}));

const HelpIconComponent: FC<HelpIconComponentProps> = ({ content }) => {
    const [helpAnchorEl, setHelpAnchorEl] = useState<HTMLButtonElement | null>(null);

    const [arrowRef, setArrowRef] = React.useState(null);
    const buttonRef = useRef(null);

    const toggleHelp = () => {
        if (helpAnchorEl) {
            setHelpAnchorEl(null);
        } else {
            setHelpAnchorEl(buttonRef.current);
        }
    };

    const arrowStyles = {
        arrow: {
            position: 'absolute',
            fontSize: 7,
            width: '3em',
            height: '3em',
            '&::before': {
                content: '""',
                margin: 'auto',
                display: 'block',
                width: 0,
                height: 0,
                borderStyle: 'solid',
            },
        },
    };

    return (
        <div className={styles.HelpIconComponent} data-testid="HelpIconComponent">
            <IconButton ref={buttonRef} sx={{ p: 0, ml: 1 }} edge="end" onClick={() => toggleHelp()}>
                <HelpOutlineOutlinedIcon color={helpAnchorEl ? 'primary' : 'inherit'} />
            </IconButton>
            <Zoom in={!!helpAnchorEl}>
                <Box>
                    <StyledPopper
                        open={!!helpAnchorEl}
                        anchorEl={helpAnchorEl}
                        placement="bottom-start"
                        modifiers={[
                            {
                                name: 'arrow',
                                enabled: true,
                                options: {
                                    element: arrowRef,
                                },
                            },
                        ]}
                    >
                        <Box component="span" className="arrow" ref={setArrowRef} sx={arrowStyles.arrow} />
                        <Box
                            component={Paper}
                            sx={{
                                maxWidth: '375px',
                                p: 1,
                            }}
                        >
                            <Typography component="div" sx={{ p: 2 }}>{helpContent[content]}</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                                <Button variant="text" onClick={() => toggleHelp()}>Ok</Button>
                            </Box>
                        </Box>
                    </StyledPopper>
                </Box>
            </Zoom>
        </div>
    );
};

export default HelpIconComponent;
